import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { postCompressed } from '@/lib/post-compressed';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import {
  Upload, ArrowRight, ArrowLeft, Check, AlertCircle,
  Loader2, FileText, ImagePlus, Search, ExternalLink, Zap
} from 'lucide-react';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface DiscoveredImage {
  url: string;
  filename: string;
  occurrences: number;
  columns: string[];
}

interface ScanResult {
  totalRows: number;
  images: DiscoveredImage[];
  parseErrors?: string[];
}

interface UploadResult {
  url: string;
  filename: string;
  status: 'uploaded' | 'failed';
  error?: string;
  fileId?: string;
}

interface DoneEvent {
  type: 'done';
  total: number;
  uploadedCount: number;
  failedCount: number;
  results: UploadResult[];
}

export function ShopifyFileUpload() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [storeId, setStoreId] = useState('');
  const [filename, setFilename] = useState('');
  
  const [isParsing, setIsParsing] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [result, setResult] = useState<DoneEvent | null>(null);
  const [activeJobId, setActiveJobId] = useState(() => sessionStorage.getItem('shopify-file-upload-job') ?? '');

  const { data: stores = [] } = useQuery<any[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stores`, { headers: authOpts.headers as HeadersInit });
      if (!res.ok) throw new Error('Failed to load stores');
      return res.json();
    },
    enabled: !!token,
  });

  const processFile = useCallback(async (file: File) => {
    setFilename(file.name);
    const text = await file.text();
    setIsParsing(true);
    try {
      const res = await postCompressed(`${BASE}/api/imports/shopify-files/scan`, { csvText: text }, { headers: authOpts.headers as any });
      if (res.status === 413) throw new Error('CSV file is too large. Please split it into smaller files.');
      const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
      if (!res.ok) throw new Error(data.error ?? 'Scan failed');
      
      setScanResult(data);
      // Pre-select all by default
      if (data.images) {
        setSelectedUrls(new Set(data.images.map((img: DiscoveredImage) => img.url)));
      }
      setStep(2);
    } catch (err: any) {
      toast({ title: 'Scan Error', description: err.message, variant: 'destructive' });
      setFilename('');
      if (fileRef.current) fileRef.current.value = '';
    } finally {
      setIsParsing(false);
    }
  }, [authOpts.headers, toast]);

  const handleFile = (file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      toast({ title: 'Invalid file', description: 'Please upload a CSV file.', variant: 'destructive' });
      return;
    }
    processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const reset = () => {
    setStep(1);
    setStoreId('');
    setFilename('');
    setScanResult(null);
    setSelectedUrls(new Set());
    setSearchQuery('');
    setResult(null);
    setProgress(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  useEffect(() => {
    if (!activeJobId || !token) return;
    let stopped = false;
    setStep(3);
    setIsRunning(true);

    const poll = async () => {
      while (!stopped) {
        try {
          const response = await fetch(`${BASE}/api/imports/shopify-files/upload/${activeJobId}`, {
            headers: authOpts.headers as HeadersInit,
            cache: 'no-store',
          });
          if (response.status === 404) {
            throw new Error('The upload job expired or the API restarted. Scan the CSV again before retrying.');
          }
          if (!response.ok) throw new Error(`Could not check upload progress (${response.status})`);
          const job = await response.json();
          if (stopped) return;
          setProgress({ current: job.current, total: job.total });
          if (job.status === 'done') {
            setResult({
              type: 'done',
              total: job.total,
              uploadedCount: job.uploadedCount,
              failedCount: job.failedCount,
              results: job.results,
            });
            setIsRunning(false);
            setProgress(null);
            sessionStorage.removeItem('shopify-file-upload-job');
            setActiveJobId('');
            return;
          }
        } catch (err: any) {
          if (!stopped && String(err.message).includes('expired')) {
            toast({ title: 'Upload Status Unavailable', description: err.message, variant: 'destructive' });
            sessionStorage.removeItem('shopify-file-upload-job');
            setActiveJobId('');
            setIsRunning(false);
            setStep(1);
            return;
          }
        }
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    };
    void poll();
    return () => { stopped = true; };
  }, [activeJobId, token, authOpts.headers, toast]);

  const runUpload = async () => {
    setIsRunning(true);
    setProgress(null);
    setResult(null);
    setStep(3);
    
    try {
      const urlsToUpload = Array.from(selectedUrls);
      const res = await postCompressed(`${BASE}/api/imports/shopify-files/upload`, {
        storeId,
        urls: urlsToUpload,
      }, { headers: authOpts.headers as any });
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
        throw new Error(data.error ?? 'Upload failed');
      }
      const data = await res.json();
      if (!data.jobId) throw new Error('Shopify upload did not start');
      sessionStorage.setItem('shopify-file-upload-job', data.jobId);
      setActiveJobId(data.jobId);
    } catch (err: any) {
      toast({ title: 'Upload Error', description: err.message, variant: 'destructive' });
      setStep(2); // Go back if complete failure before stream starts
      setIsRunning(false);
      setProgress(null);
    }
  };

  const filteredImages = useMemo(() => {
    if (!scanResult) return [];
    if (!searchQuery.trim()) return scanResult.images;
    const lower = searchQuery.toLowerCase();
    return scanResult.images.filter(img => 
      img.filename.toLowerCase().includes(lower) || 
      img.url.toLowerCase().includes(lower)
    );
  }, [scanResult, searchQuery]);

  const handleToggleAll = () => {
    if (!scanResult) return;
    const allFilteredUrls = filteredImages.map(img => img.url);
    
    // Are all currently filtered items selected?
    const allSelected = allFilteredUrls.every(url => selectedUrls.has(url));
    
    setSelectedUrls(prev => {
      const next = new Set(prev);
      if (allSelected) {
        // Deselect all filtered
        allFilteredUrls.forEach(url => next.delete(url));
      } else {
        // Select all filtered
        allFilteredUrls.forEach(url => next.add(url));
      }
      return next;
    });
  };

  const toggleUrl = (url: string) => {
    setSelectedUrls(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const selectedStore = stores.find((s: any) => s.id === storeId);
  const canProceedStep1 = !!storeId && filename !== '';
  const canProceedStep2 = selectedUrls.size > 0 && !!storeId;
  
  const isAllFilteredSelected = filteredImages.length > 0 && filteredImages.every(img => selectedUrls.has(img.url));
  const isSomeFilteredSelected = filteredImages.some(img => selectedUrls.has(img.url));

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Shopify File Upload</h1>
          <p className="text-muted-foreground mt-1">
            Scan a Globo Options CSV for external image URLs and batch upload them to Shopify Content &gt; Files.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {[
            { n: 1, label: 'Upload CSV' },
            { n: 2, label: 'Select Images' },
            { n: 3, label: 'Run Upload' },
          ].map(({ n, label }, i) => (
            <React.Fragment key={n}>
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border transition-all",
                  step === n ? "bg-primary text-primary-foreground border-primary shadow-[0_0_10px_rgba(var(--primary),0.4)]" :
                  step > n ? "bg-primary/20 text-primary border-primary/40" :
                  "bg-muted/50 text-muted-foreground border-border"
                )}>
                  {step > n ? <Check className="w-4 h-4" /> : n}
                </div>
                <span className={cn("text-sm font-medium hidden sm:block", step === n ? "text-foreground" : "text-muted-foreground")}>{label}</span>
              </div>
              {i < 2 && <div className={cn("flex-1 h-px", step > n ? "bg-primary/40" : "bg-muted")} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 1: Store + Upload ── */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Store selector */}
            <div className="glass-panel rounded-2xl p-6 space-y-4">
              {stores.length === 0 ? (
                <p className="text-sm text-muted-foreground">No stores connected. Add a store first.</p>
              ) : (
                <ShopifyTargetStoreSelector
                  stores={stores}
                  value={storeId}
                  onChange={setStoreId}
                  label="Destination Store"
                  variant="tiles"
                />
              )}
            </div>

            {/* File upload */}
            <div className="glass-panel rounded-2xl p-6 space-y-4">
              <h2 className="font-semibold text-foreground">Upload Globo Options CSV</h2>
              <div className="flex items-start gap-3 text-sm text-primary bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
                <ImagePlus className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>The selected Shopify connection needs the <strong>read_files</strong> and <strong>write_files</strong> API permissions.</span>
              </div>
              <div
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={cn(
                  "relative border-2 border-dashed rounded-2xl p-12 flex flex-col items-center gap-4 cursor-pointer transition-all",
                  isDragging ? "border-primary bg-primary/5" : "border-border hover:border-border hover:bg-muted/50",
                  !storeId && "opacity-50 pointer-events-none"
                )}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                  disabled={!storeId}
                />
                {isParsing ? (
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                ) : filename ? (
                  <FileText className="w-10 h-10 text-primary" />
                ) : (
                  <Upload className="w-10 h-10 text-muted-foreground" />
                )}
                <div className="text-center">
                  {isParsing ? (
                    <p className="text-sm text-muted-foreground">Scanning CSV for image URLs...</p>
                  ) : filename ? (
                    <p className="font-medium text-foreground">{filename}</p>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">Drop your CSV here or click to browse</p>
                      <p className="text-sm text-muted-foreground mt-1">Please select a store above before uploading</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Review + Select ── */}
        {step === 2 && scanResult && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-6 space-y-6">
              
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-foreground">Review Discovered Images</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Found {scanResult.images.length} unique images across {scanResult.totalRows.toLocaleString()} rows.
                  </p>
                </div>
                <div className="px-4 py-2 bg-muted/30 border border-border/50 rounded-xl text-sm font-medium">
                  Store: <span className="text-primary">{selectedStore?.nickname ?? storeId}</span>
                </div>
              </div>

              {/* Data Table */}
              <div className="border border-border/60 rounded-xl overflow-hidden flex flex-col max-h-[600px] bg-background shadow-sm">
                <div className="p-3 border-b border-border/60 flex justify-between items-center bg-muted/10">
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox"
                      checked={isAllFilteredSelected}
                      ref={input => {
                        if (input) input.indeterminate = isSomeFilteredSelected && !isAllFilteredSelected;
                      }}
                      onChange={handleToggleAll}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary/50 cursor-pointer"
                    />
                    <span className="text-sm font-medium">{selectedUrls.size} images selected</span>
                  </div>
                  <div className="relative w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input 
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 rounded-lg bg-background border border-border/60 text-sm focus:outline-none focus:border-primary/50 transition-colors"
                      placeholder="Search URLs or filenames..."
                    />
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredImages.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">
                      No images found matching your search.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-muted/30 z-10 border-b border-border/60 backdrop-blur-md">
                        <tr>
                          <th className="w-12 px-4 py-3"></th>
                          <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Filename</th>
                          <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Source Columns</th>
                          <th className="text-right px-4 py-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Occurrences</th>
                          <th className="w-16 px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredImages.map(img => (
                          <tr key={img.url} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3">
                              <input 
                                type="checkbox"
                                checked={selectedUrls.has(img.url)}
                                onChange={() => toggleUrl(img.url)}
                                className="w-4 h-4 rounded border-border text-primary focus:ring-primary/50 cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3 min-w-[200px]">
                              <div className="font-medium text-foreground truncate max-w-[300px]" title={img.filename}>
                                {img.filename}
                              </div>
                              <div className="text-xs text-muted-foreground truncate max-w-[300px] mt-0.5" title={img.url}>
                                {img.url}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {img.columns.map(c => (
                                  <span key={c} className="px-1.5 py-0.5 rounded bg-muted/60 text-[10px] text-muted-foreground border border-border/50">{c}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                              {img.occurrences}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <a 
                                href={img.url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                title="View original image"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Warnings / Callouts */}
              {scanResult.parseErrors && scanResult.parseErrors.length > 0 && (
                <div className="flex items-start gap-3 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Parse Warnings</p>
                    <p className="mt-0.5 opacity-90">{scanResult.parseErrors.length} CSV formatting warnings were found. Image URL discovery continued across all readable fields.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => { setStep(1); setScanResult(null); }}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Start Over
              </Button>
              <Button onClick={runUpload} disabled={!canProceedStep2}>
                Upload {selectedUrls.size} Images <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Run Upload ── */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-8 flex flex-col items-center justify-center min-h-[300px] text-center">
              
              {isRunning && (
                <div className="space-y-6 w-full max-w-md mx-auto">
                  <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Uploading to Shopify</h2>
                    <p className="text-sm text-muted-foreground mt-2">
                      Transferring images to {selectedStore?.nickname ?? storeId}...
                    </p>
                  </div>
                  
                  {progress && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-foreground">{progress.current} / {progress.total}</span>
                        <span className="text-primary">{Math.round((progress.current / progress.total) * 100)}%</span>
                      </div>
                      <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all duration-300 ease-out"
                          style={{ width: `${(progress.current / progress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {result && (
                <div className="space-y-8 w-full max-w-4xl mx-auto">
                  <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <Check className="w-8 h-8 text-emerald-500" />
                  </div>
                  
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Upload Complete</h2>
                    <p className="text-muted-foreground mt-2">
                      Successfully uploaded <span className="font-semibold text-foreground">{result.uploadedCount}</span> images.
                      {result.failedCount > 0 && <span className="text-red-400 ml-1">Failed to upload {result.failedCount} images.</span>}
                    </p>
                  </div>

                  {/* Results Table */}
                  {result.results.length > 0 && (
                    <div className="border border-border/60 rounded-xl overflow-hidden bg-background text-left shadow-sm">
                      <div className="max-h-[400px] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-muted/30 z-10 border-b border-border/60 backdrop-blur-md">
                            <tr>
                              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Status</th>
                              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Filename</th>
                              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.results.map((r, i) => (
                              <tr key={i} className="border-b border-border/30 last:border-0 hover:bg-muted/10 transition-colors">
                                <td className="px-4 py-3 w-32">
                                  {r.status === 'uploaded' ? (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                                      <Check className="w-3 h-3" /> Uploaded
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-bold bg-red-500/10 text-red-500 border border-red-500/20">
                                      <AlertCircle className="w-3 h-3" /> Failed
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 font-medium text-foreground">
                                  {r.filename}
                                </td>
                                <td className="px-4 py-3 text-muted-foreground text-xs">
                                  {r.status === 'uploaded' && r.fileId ? (
                                    <span className="font-mono">{r.fileId}</span>
                                  ) : (
                                    <span className="text-red-400">{r.error || 'Unknown error'}</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-center pt-4">
                    <Button onClick={reset}>
                      <Upload className="w-4 h-4 mr-2" /> Upload Another CSV
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}
